import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {mapToNewBranchCreationAPI, mapToUserProfile, mapToGuestProfile} from './registration.utils'
import {
    createNewBranch,
    userProfile,
    inviteCoworkers,
    userRegisterBranchAdmin,
    guestRegistrationApi,
    guestSecurityCode,
    resendSecurityCodeApi
} from 'services/registration.service'

export interface registration {
    isLoading: boolean
    error: any
    response: object
    payload: object
    publicFunctionInput: any
    inviteCoworker: object
    inviteLoader: boolean
    inviteError: any
    guestotp: object
}

const initialState: registration = {
    isLoading: false,
    error: null,
    response: {},
    payload: {},
    publicFunctionInput: {},
    guestotp: {},
    inviteCoworker: {},
    inviteLoader: false,
    inviteError: null
}

const throwError = (data) => {
    if (!data.successFlag || typeof data === 'string') {
        throw Error(`${data?.errorList[0]?.text}`)
    }
}

export const register = createAsyncThunk('register', async (payload: any, {rejectWithValue}) => {
    try {
        const finalData = {
            payload,
            response: {}
        }
        if (payload.isBranchExist.toLowerCase() === 'guest') {
            const guestUserProfile = mapToGuestProfile(payload)
            const response = await guestRegistrationApi(guestUserProfile)
            throwError(response.data)
            finalData.response = response.data
        } else if (payload.isBranchExist.toLowerCase() === 'yes') {
            const userProfileRequest = mapToUserProfile(payload)
            let response
            if (payload.registerBy === 'admin') {
                response = await userRegisterBranchAdmin(userProfileRequest)
            } else {
                response = await userProfile({...userProfileRequest, isInviteUser: payload.isInviteUser})
            }
            throwError(response.data)
            finalData.response = response.data
        } else {
            const newBranchApiPayload = mapToNewBranchCreationAPI(payload)
            const {data} = await createNewBranch(newBranchApiPayload)
            throwError(data)
            finalData.response = data
        }
        return finalData
    } catch (err: any) {
        if (err.code === 'ERR_BAD_RESPONSE') {
            return rejectWithValue('500 - Something Went Wrong, Internal Server Error')
        }
        return rejectWithValue(err.message)
    }
})
export const fetchGuestVerifySecurityCode = createAsyncThunk('verifycode', async (payload: any, {rejectWithValue}) => {
    try {
        const {data} = await guestSecurityCode(payload)
        throwError(data)
        return 'matched'
    } catch (err: any) {
        if (err.status >= 400 && err.status < 500) {
            const msg: string =
                String(err?.response?.errors[0]?.field) + ' ' + String(err?.response?.errors[0]?.defaultMessage)
            return rejectWithValue(msg)
        } else if (err.status > 500) {
            const errorMsg = err.errorList[0].text
            return rejectWithValue(errorMsg)
        }
        return rejectWithValue(err.message)
    }
})
export const fetchResendSecurityCode = createAsyncThunk('resendCode', async (payload: any, {rejectWithValue}) => {
    try {
        const {data} = await resendSecurityCodeApi(payload)
        throwError(data)
        return data.response
    } catch (err: any) {
        if (err.status >= 400 && err.status < 500) {
            const msg: string =
                String(err?.response?.errors[0]?.field) + ' ' + String(err?.response?.errors[0]?.defaultMessage)
            return rejectWithValue(msg)
        } else if (err.status > 500) {
            const errorMsg = err.errorList[0].text
            return rejectWithValue(errorMsg)
        }
        return rejectWithValue(err.message)
    }
})
export const fetchinviteCoworkers = createAsyncThunk('inviteCoworkers', async (payload: any, {rejectWithValue}) => {
    try {
        const finalData = {
            payload,
            response: {}
        }
        const {data} = await inviteCoworkers(payload)
        throwError(data)
        finalData.response = data.response
        return finalData
    } catch (err: any) {
        if (err.status >= 400 && err.status < 500) {
            const msg: string =
                String(err?.response?.errors[0]?.field) + ' ' + String(err?.response?.errors[0]?.defaultMessage)
            return rejectWithValue(msg)
        } else if (err.status > 500) {
            return rejectWithValue('Something went wrong')
        }
        return rejectWithValue(err.message)
    }
})

const registrationSlice = createSlice({
    name: 'registration',
    initialState,
    reducers: {
        storePublicFunctionInput: (state, action) => {
            state.publicFunctionInput = action.payload
        },
        setResetError: (state) => {
            state.error = null
        }
    },
    extraReducers: function (builder) {
        builder.addCase(register.pending, (state, action) => {
            state.isLoading = true
            state.error = null
        })
        builder.addCase(register.fulfilled, (state, action) => {
            const data: any = action.payload
            state.isLoading = false
            state.response = data.response
            state.payload = data.payload
            state.error = null
        })
        builder.addCase(register.rejected, (state, action: any) => {
            state.isLoading = false
            state.response = {}
            state.payload = {}
            state.error = action.payload
        })
        builder.addCase(fetchGuestVerifySecurityCode.pending, (state: any, action) => {
            state.isLoading = true
            state.error = null
        })
        builder.addCase(fetchGuestVerifySecurityCode.fulfilled, (state: any, action) => {
            state.isLoading = false
            state.response = action.payload
            state.error = null
        })
        builder.addCase(fetchGuestVerifySecurityCode.rejected, (state: any, action: any) => {
            state.isLoading = false
            state.response = {}
            state.payload = {}
            state.error = action.payload
        })
        builder.addCase(fetchResendSecurityCode.pending, (state: any, action) => {
            state.isLoading = true
            state.response = null
            state.error = null
            state.guestotp.response = null
            state.guestotp.error = null
        })
        builder.addCase(fetchResendSecurityCode.fulfilled, (state: any, action) => {
            state.isLoading = false
            state.response = action.payload
            state.guestotp.response = action.payload
            state.guestotp.error = null
            state.error = null
        })
        builder.addCase(fetchResendSecurityCode.rejected, (state: any, action: any) => {
            state.isLoading = false
            state.response = null
            state.guestotp.response = null
            state.guestotp.error = action.payload
            state.error = action.payload
        })
        builder.addCase(fetchinviteCoworkers.pending, (state: any, action) => {
            state.inviteLoader = true
            state.inviteCoworker = null
            state.inviteError = null
        })
        builder.addCase(fetchinviteCoworkers.fulfilled, (state: any, action) => {
            state.inviteLoader = false
            state.inviteCoworker = action.payload
            state.inviteError = null
        })
        builder.addCase(fetchinviteCoworkers.rejected, (state: any, action: any) => {
            state.inviteLoader = false
            state.inviteCoworker = null
            state.inviteError = action.payload
        })
    }
})

export const {storePublicFunctionInput, setResetError} = registrationSlice.actions
export const {reducer: registrationReducer} = registrationSlice
